import { createClient, PostgrestError, Session } from '@supabase/supabase-js'
import { isNumber } from 'lodash'
import { logger } from './logger'
import { HOSTNAME } from '../../lib/config'
import { parseError } from './stream'
import {
  forceLocalSignOut as clearLocalAuthSession,
  probeFunctionSession,
  signOutLocally,
  type AuthClient,
} from './auth-session.helpers'
import {
  SIGNED_URL_VALIDITY,
  SUPABASE_PUBLISHABLE_KEY,
  SUPABASE_URL,
  REDEEM_CODE_VALIDITY,
  MAX_SIGNED_URL_VALIDITY,
} from '../constants/supabase.constants'
import {
  EXPERIENCES_TABLE,
  EXPERIENCE_CONTENT_TABLE,
  GEOXP_AUDIO_BUCKET,
  GEOXP_SPOTS_TABLE,
  GEOXP_VIDEO_BUCKET,
  PATTERNS_TABLE,
  USER_EXPERIENCES_TABLE,
} from '../constants/db.constants'
import {
  SupabaseResponse,
  DatabaseInsertResult,
  DatabaseSelectResult,
  SignInResult,
  UserProfile,
  AgamiSpot,
  ExperienceContentData,
  AgamiUserExperience,
  AgamiExperience,
  AgamiPattern,
  User,
  AccessCode,
  RedeemCode,
  LandingPageCode,
} from '../../types/supabase'
import { GeoXpConfig } from '../../types/geoXp'
import { buildSigninPath } from './signin-redirect.helpers'

// **** CLIENT ****
export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY)

// **** USER ****
export const signInWithEmail = async ({ email }: { email: string }): Promise<SupabaseResponse> => {
  const { data, error } = await supabase.auth.signInWithOtp({ email })
  return { data, error }
}

export const signInWithEmailAndPassword = async ({
  email,
  password,
}: {
  email: string
  password: string
}): Promise<SignInResult> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  return { data, error }
}

export const resetPasswordEmail = async ({ email }: { email: string }): Promise<SupabaseResponse> => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${HOSTNAME}` })
  return { data, error }
}

export const updateUserPassword = async ({
  password,
  passwordConfirm,
}: {
  password: string
  passwordConfirm: string
}): Promise<SupabaseResponse> => {
  if (!password) {
    return { error: 'Password missing' }
  }

  if (password !== passwordConfirm) {
    return { error: 'Passwords do not match' }
  }

  const { data, error } = await supabase.auth.updateUser({ password })
  return { data, error }
}

export const forceLocalSignOut = async () => {
  await clearLocalAuthSession(supabase.auth as unknown as AuthClient)
}

export const signOut = async () => {
  const remoteError = await signOutLocally(supabase.auth as unknown as AuthClient)
  if (remoteError) {
    logger.error(remoteError, 'SignOut')
    window.location.href = '/'
  }
  return { error: remoteError }
}

const recoverInvalidSession = async (error: unknown): Promise<void> => {
  const session = await probeFunctionSession(supabase.auth as unknown as AuthClient, error)
  if (session !== 'invalid') return

  await forceLocalSignOut()
  const { pathname, search, hash } = window.location
  window.location.href = buildSigninPath(pathname + search + hash, { session_expired: 'true' })
}

export const getSupabaseSession = async (): Promise<SupabaseResponse<{ session: Session }>> => {
  const { data, error } = await supabase.auth.getSession()
  return { data: data.session ? { session: data.session } : undefined, error }
}

export const getCompleteUserProfile = async (): Promise<SupabaseResponse<UserProfile>> => {
  const { data, error } = await supabase.functions.invoke('user-profile')

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const checkAdmin = async (): Promise<{ isAdmin: boolean; error: PostgrestError | null }> => {
  const { data, error } = await supabase.rpc('get_user_role_id')
  const isAdmin = data === 1
  return { isAdmin, error }
}

export const updateUser = async ({
  attributes,
  id,
}: {
  attributes: Record<string, unknown>
  id: string
}): Promise<SupabaseResponse> => {
  if (!attributes) {
    return { error: 'Provide an userAttributes object' }
  }

  const { data, error } = await supabase.from('profiles').update(attributes).eq('id', id)

  return { data, error }
}

export const inviteUserWithCode = async ({
  email,
  role,
  code,
}: {
  email: string
  role: number
  code: string
}): Promise<SupabaseResponse> => {
  if (!email || !role || !code) {
    return { error: 'Incorrect data provided' }
  }

  const { data, error } = await supabase.functions.invoke('invite-new-user', {
    body: JSON.stringify({ email, role, code }),
  })

  if (!error) {
    return { data }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const sendUpgradeCode = async ({
  email,
  role,
  roleLabel,
  code,
}: {
  email: string
  role: number
  roleLabel: string
  code: string
}): Promise<SupabaseResponse> => {
  if (!email || !role || !code) {
    return { error: 'Incorrect data provided' }
  }

  const { data, error } = await supabase.functions.invoke('generate-upgrade-code', {
    body: JSON.stringify({
      email,
      role,
      roleLabel,
      code,
    }),
  })

  if (!error) {
    return { data }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

// *** DB QUERIES ***
export const dbInsert = async ({
  table,
  insertData,
}: {
  table: string
  insertData: Record<string, unknown>
}): Promise<DatabaseInsertResult> => {
  const { data, error } = await supabase.from(table).insert(insertData).select()
  return { data, error }
}

export const singleDbInsert = async ({
  table,
  insertData,
}: {
  table: string
  insertData: Record<string, unknown>
}): Promise<DatabaseInsertResult> => {
  const { data, error } = await supabase.from(table).insert(insertData).select().single()
  return { data, error }
}

export const dbUpsert = async ({
  table,
  upsertData,
  upsertOptions,
}: {
  table: string
  upsertData: Record<string, unknown>[]
  upsertOptions?: Record<string, unknown>
}): Promise<DatabaseInsertResult> => {
  const { data, error } = await supabase.from(table).upsert(upsertData, upsertOptions).select()
  return { data, error }
}

export const singleDbUpsert = async <T extends Record<string, unknown>>({
  table,
  upsertData,
}: {
  table: string
  upsertData: T
}): Promise<DatabaseInsertResult<T>> => {
  const { data, error } = await supabase.from(table).upsert(upsertData).select().single()
  return { data, error }
}

export const dbSelect = async ({ table }: { table: string }): Promise<DatabaseSelectResult> => {
  const { data, error } = await supabase.from(table).select('*')
  return { data, error }
}

export const dbSelectByUser = async ({
  table,
  userId,
}: {
  table: string
  userId: string
}): Promise<DatabaseSelectResult> => {
  const { data, error } = await supabase.from(table).select('*').eq('user_id', userId)
  return { data, error }
}

// **** BUCKETS ****
export const listBuckets = async (): Promise<{
  data?: Array<{
    id: string
    name: string
    owner: string
    public: boolean
    created_at: string
    updated_at: string
    file_size_limit?: number
    allowed_mime_types?: string[]
  }> | null
  error?: { message: string; statusCode?: string } | null
}> => {
  const { data, error } = await supabase.storage.listBuckets()
  return { data, error }
}

export const getBucketInfo = async (
  bucketId: string
): Promise<{
  data?: {
    id: string
    name: string
    owner: string
    public: boolean
    created_at: string
    updated_at: string
    file_size_limit?: number
    allowed_mime_types?: string[]
  } | null
  error?: { message: string; statusCode?: string } | null
}> => {
  const { data, error } = await supabase.storage.getBucket(bucketId)
  return { data, error }
}

// **** CUSTOM HELPERS ****
export const uploadFile = async ({
  bucketId,
  file,
  filename,
  cacheControl,
  contentType,
  upsert,
}: {
  bucketId: string
  file: File | string
  filename: string
  cacheControl?: string
  contentType?: string
  upsert?: boolean
}): Promise<{
  data?: { path: string; fullPath: string; id: string } | null
  error?: { message: string; statusCode?: string } | null
}> => {
  const { data, error } = await supabase.storage.from(bucketId).upload(filename, file, {
    ...(typeof cacheControl === 'string' && { cacheControl }),
    ...(typeof upsert === 'boolean' && { upsert }),
    ...(typeof contentType === 'string' && { contentType }),
  })
  return { data, error }
}

export const removeFile = async ({
  bucketId,
  filename,
}: {
  bucketId: string
  filename: string
}): Promise<{
  data?: Array<{
    name: string
    id: string | null
    updated_at: string | null
    created_at: string | null
    last_accessed_at: string | null
    metadata: Record<string, unknown> | null
  }> | null
  error?: { message: string; statusCode?: string } | null
}> => {
  const { data, error } = await supabase.storage.from(bucketId).remove([filename])
  return { data, error }
}

export const updateFile = async ({
  bucketId,
  file,
  filename,
  contentType,
}: {
  bucketId: string
  file: File
  filename: string
  contentType?: string
}): Promise<{
  data?: { path: string; fullPath: string; id: string } | null
  error?: { message: string; statusCode?: string } | null
}> => {
  const { data, error } = await supabase.storage.from(bucketId).update(filename, file, {
    cacheControl: '3600',
    upsert: true,
    ...(contentType && { contentType }),
  })
  return { data, error }
}

export const getAudioFileUrl = async ({
  bucketId,
  filePath,
}: {
  bucketId: string
  filePath: string
}): Promise<{
  data?: { signedUrl: string } | null
  error?: { message: string; statusCode?: string } | null
}> => {
  const { data, error } = await supabase.storage.from(bucketId).createSignedUrl(filePath, SIGNED_URL_VALIDITY)
  return { data, error }
}

export const getGeoXpConfig = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<{
  data?: GeoXpConfig | null
  error?: Record<string, unknown> | string | number | boolean | unknown[] | null
}> => {
  if (!experienceId) {
    return { error: 'No experienceId provided' }
  }

  const { data, error } = await supabase.functions.invoke('get-geoxp-config', {
    body: JSON.stringify({ experienceId }),
  })

  if (!error) {
    return { data }
  }

  const { body } = error.context
  if (!body) {
    return { data, error }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const getUserExperiences = async ({
  userId,
}: {
  userId: string
}): Promise<DatabaseSelectResult<AgamiUserExperience[]>> => {
  const { data, error } = await supabase
    .from(USER_EXPERIENCES_TABLE)
    .select(
      `
      *,
      experiences!inner(
        is_active,
        name,
        owner_id,
        created_at,
        is_default,
        profiles!experiences_owner_id_fkey(name, surname)
      )
    `
    )
    .eq('user_id', userId)
    .not('is_enabled', 'is', false)
    .eq('experiences.is_canceled', false)

  return { data, error }
}

export const getExperienceDetails = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<DatabaseSelectResult<AgamiExperience>> => {
  const { data, error } = await supabase.from('experiences').select('*').eq('id', experienceId).single()

  return { data, error }
}

export const getUserOwnedExperiencesWithoutDefault = async ({
  userId,
}: {
  userId: string
}): Promise<DatabaseSelectResult<AgamiExperience[]>> => {
  const { data, error } = await supabase
    .from('experiences')
    .select('*')
    .eq('owner_id', userId)
    .not('is_default', 'is', true)
    .eq('is_canceled', false)
  return { data, error }
}

export const getUserOwnedExperiences = async ({
  userId,
}: {
  userId: string
}): Promise<DatabaseSelectResult<AgamiExperience[]>> => {
  const { data, error } = await supabase
    .from('experiences')
    .select('*')
    .eq('owner_id', userId)
    .eq('is_canceled', false)
  return { data, error }
}

export const getUserOwnedExperience = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<SupabaseResponse<AgamiExperience>> => {
  const { data, error } = await supabase.functions.invoke('get-experience-details', {
    body: JSON.stringify({ experienceId }),
  })

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const getExperienceSpotsCount = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<{ count: number | null; data?: AgamiSpot[] | null; error?: PostgrestError | null }> => {
  const { count, data, error } = await supabase
    .from('experience_content_spots')
    .select('*, geoxp_spots!inner(*)', { count: 'exact', head: false })
    .eq('experience_id', experienceId)
    .eq('is_canceled', false)
    .eq('geoxp_spots.is_canceled', false)
    .eq('geoxp_spots.is_draft', false)
  return { count, data, error }
}

export const downloadGeoXpExperience = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<SupabaseResponse> => {
  if (!experienceId) {
    return { error: 'No experienceId provided' }
  }

  const { data, error } = await supabase.functions.invoke('download-geoxp-experience', {
    body: JSON.stringify({ experienceId }),
  })

  await recoverInvalidSession(error)

  return { data, error }
}

type ShareExperiencePayload = {
  email: string
  experienceId: string
  role?: number
  revoke?: boolean
  sharingId?: string | null
}

export const shareExperience = async (payload: ShareExperiencePayload): Promise<SupabaseResponse> => {
  const { email, experienceId } = payload

  if (!email || !experienceId) {
    return { error: 'No data provided' }
  }

  const { data, error } = await supabase.functions.invoke('share-experience', {
    body: JSON.stringify(payload),
  })

  if (!error) {
    return { data }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const getSpotTypes = async (): Promise<DatabaseSelectResult> => {
  const { data, error } = await supabase.from('spot_types').select('*').eq('is_active', true)
  return { data, error }
}

export const checkNewAgamiManualCode = async ({
  code,
  experiences,
}: {
  code: string
  experiences: string[]
}): Promise<{ available: boolean; error: PostgrestError | null }> => {
  const { data, error } = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    .select('*, geoxp_spots!inner(*)')
    .in('experience_id', experiences)
    .eq('manual_code', code)
    .eq('geoxp_spots.is_canceled', false)
  return { available: data?.length === 0, error }
}

export const checkExistingAgamiManualCode = async ({
  code,
  experiences,
  spotId,
}: {
  code: string
  experiences: string[]
  spotId: string
}): Promise<{ available: boolean; error: PostgrestError | null }> => {
  const { data, error } = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    .select('*, geoxp_spots!inner(*)')
    .in('experience_id', experiences)
    .eq('manual_code', code)
    .eq('geoxp_spots.is_canceled', false)
    .neq('geoxp_spots.id', spotId)
  return { available: data?.length === 0, error }
}

export const getExperiencesForSpot = async ({
  spot_id,
}: {
  spot_id: string
}): Promise<DatabaseSelectResult> => {
  const { data, error } = await supabase.from(EXPERIENCE_CONTENT_TABLE).select('*').eq('spot_id', spot_id)
  return { data, error }
}

export const cancelManyExperienceContent = async ({
  ids,
}: {
  ids: string[]
}): Promise<DatabaseInsertResult> => {
  if (!ids || ids?.length === 0) {
    return { error: { message: 'Missing exp ID array' } as PostgrestError }
  }

  const { data, error } = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    .update({ is_canceled: true })
    .in('id', ids)
    .select()
  return { data, error }
}

export const checkRedeemCode = async ({
  code,
}: {
  code: string
}): Promise<DatabaseSelectResult<RedeemCode[]>> => {
  if (!code) {
    return { error: { message: 'Provide redeem code' } as PostgrestError }
  }

  const { data, error } = await supabase.from('redeem_codes').select('*').eq('code', code)
  return { data, error }
}

// UPGRADE CODE
export const redeemUpgradeCode = async ({
  email,
  code,
}: {
  email: string
  code: string
}): Promise<DatabaseInsertResult> => {
  if (!email || !code) {
    return { error: { message: 'Provide user email and redeem code' } as PostgrestError }
  }

  const { data, error } = await supabase
    .from('redeem_codes')
    .update({ redeemed: true, redeemed_at: new Date().toISOString() })
    .eq('user_email', email)
    .eq('code', code)
    .eq('redeemed', false)
    .select()

  if (!data || data.length === 0) {
    return {
      error: {
        message: 'Your code is invalid or has already been used! Contact us for a new code.',
      } as PostgrestError,
    }
  }

  const issueDate = new Date(data[0].issued_at).getTime()

  const now = Date.now()

  const diff = (now - issueDate) / (1000 * 3600 * 24) // days

  if (diff > REDEEM_CODE_VALIDITY) {
    return {
      error: { message: 'Your code older than 30 days. Contact us for a new code.' } as PostgrestError,
    }
  }

  return { data: data[0], error }
}

export const getCodesList = async (): Promise<DatabaseSelectResult<RedeemCode[]>> => {
  const { data, error } = await supabase.from('redeem_codes').select()
  return { data, error }
}

// **** LANDING PAGE CODES ****
// RLS filters to the calling user; no manual user_id filter needed.

export const DUPLICATE_LANDING_CODE = 'DUPLICATE_LANDING_CODE'

const normaliseLandingError = (error: PostgrestError | null): PostgrestError | null => {
  if (error?.code === '23505') {
    return Object.assign(Object.create(Object.getPrototypeOf(error)), error, {
      message: DUPLICATE_LANDING_CODE,
    })
  }
  return error
}

export const getLandingPageCodes = async (): Promise<DatabaseSelectResult<LandingPageCode[]>> => {
  const { data, error } = await supabase
    .from('landing_page_codes')
    .select('*')
    .order('created_at', { ascending: false })
  return { data, error }
}

export const createLandingPageCode = async (payload: {
  code: string
  url: string
  notes?: string | null
  isDefault?: boolean
}): Promise<DatabaseInsertResult<LandingPageCode>> => {
  const { data, error } = await supabase
    .from('landing_page_codes')
    .insert({
      code: payload.code,
      url: payload.url,
      notes: payload.notes ?? null,
      is_default: payload.isDefault ?? false,
    })
    .select()
    .single()
  return { data, error: normaliseLandingError(error) }
}

export const updateLandingPageCode = async ({
  id,
  attributes,
}: {
  id: string
  attributes: Partial<Pick<LandingPageCode, 'code' | 'url' | 'is_default' | 'notes'>>
}): Promise<DatabaseInsertResult<LandingPageCode>> => {
  const { data, error } = await supabase
    .from('landing_page_codes')
    .update(attributes)
    .eq('id', id)
    .select()
    .single()
  return { data, error: normaliseLandingError(error) }
}

export const deleteLandingPageCode = async ({ id }: { id: string }): Promise<DatabaseInsertResult> => {
  const { data, error } = await supabase.from('landing_page_codes').delete().eq('id', id).select()
  return { data, error }
}

export const setSkipOnboarding = async ({ userId }: { userId: string }): Promise<DatabaseInsertResult> => {
  const { data, error } = await supabase.from('profiles').update({ skip_onboarding: true }).eq('id', userId)
  return { data, error }
}

export const deleteExperience = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<{
  data?: { expTable: AgamiExperience[] | null; expContentTable: ExperienceContentData[] | null }
  error?: PostgrestError | null
}> => {
  // step 1 - delete experience
  const expTableUpdate = await supabase
    .from(EXPERIENCES_TABLE)
    .update({ is_canceled: true })
    .eq('id', experienceId)
    .select()

  if (expTableUpdate.error) {
    return {
      error: expTableUpdate.error,
    }
  }

  // step 2 - delete agami-experience relations associated with this experience
  const expContentTableUpdate = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    .update({ is_canceled: true })
    .eq('experience_id', experienceId)
    .select()

  if (expContentTableUpdate.error) {
    return {
      error: expContentTableUpdate.error,
    }
  }

  return {
    data: {
      expTable: expTableUpdate.data,
      expContentTable: expContentTableUpdate.data,
    },
  }
}

export const getAdminAgamiList = async ({
  search,
  range,
  sort,
}: {
  search?: string
  range?: [number, number]
  sort?: [string, { ascending: boolean }]
}): Promise<{ data?: AgamiSpot[]; count?: number; error?: PostgrestError | null }> => {
  const { data, error } = await supabase.functions.invoke('get-admin-spots', {
    body: JSON.stringify({ search, range, sort }),
  })

  if (!error) {
    return { ...data }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { ...data, error: parsedError }
}

export const removeAgamiFromExperience = async ({
  spotId,
  experienceId,
}: {
  spotId: string
  experienceId: string
}): Promise<DatabaseInsertResult> => {
  if (!spotId || !experienceId) {
    return { error: { message: 'Missing spotId or experienceId' } as PostgrestError }
  }

  const { data, error } = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    // set is_canceled to true and clear manual_code to avoid conflicts
    .update({ is_canceled: true, manual_code: null })
    .eq('experience_id', experienceId)
    .eq('spot_id', spotId)
    .select()

  return { data, error }
}

export const getUserExpAccessCodes = async ({
  userId,
}: {
  userId: string
}): Promise<DatabaseSelectResult<AccessCode[]>> => {
  const { data, error } = await supabase
    .from('access_codes')
    .select('*')
    .eq('created_by', userId)
    .not('is_deleted', 'is', true)
  return { data, error }
}

export const createExpAccessCode = async (formData: {
  experienceId?: string
  expiresAt?: string
}): Promise<SupabaseResponse> => {
  if (!formData?.experienceId) {
    return {
      error: 'Must provide an experience to generate code',
    }
  }

  const payload = {
    ...formData,
    ...(formData.expiresAt && { expiresAt: new Date(formData.expiresAt).toISOString() }),
  }

  const { data, error } = await supabase.functions.invoke('access-code-create', {
    body: JSON.stringify(payload),
  })

  if (!error) {
    return { data }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const deleteAccessCode = async ({
  accessCodeId,
}: {
  accessCodeId: string
}): Promise<DatabaseInsertResult> => {
  if (!accessCodeId) {
    return { error: { message: 'Missing accessCodeId' } as PostgrestError }
  }
  const { data, error } = await supabase
    .from('access_codes')
    .update({ is_deleted: true })
    .eq('id', accessCodeId)
    .select()
  return { data, error }
}

export const getUserPatterns = async ({
  user,
}: {
  user: User
}): Promise<DatabaseSelectResult<AgamiPattern[]>> => {
  if (!user?.id) {
    return { error: { message: 'Missing user' } as PostgrestError }
  }

  if (user.role_id !== 1 && user.role_id !== 2 && user.role_id !== 4) {
    return { error: { message: 'Unauthorized user' } as PostgrestError }
  }

  const { data, error } = await supabase
    .from(PATTERNS_TABLE)
    .select('*')
    .eq('creator_id', user.id)
    .not('is_deleted', 'is', true)
  return { data, error }
}

export const createOrEditPattern = async ({
  upsertData,
}: {
  upsertData: Record<string, unknown>
}): Promise<DatabaseInsertResult> => {
  if (!upsertData) {
    return { error: { message: 'No upsertData provided' } as PostgrestError }
  }

  // insert in pattenrs tables
  const { data, error } = await singleDbUpsert({
    table: PATTERNS_TABLE,
    upsertData,
  })

  return { data, error }
}

export const deleteUserPattern = async ({
  patternId,
}: {
  patternId: string
}): Promise<DatabaseInsertResult> => {
  if (!patternId) {
    return { error: { message: 'Missing patternId' } as PostgrestError }
  }
  const { data, error } = await supabase
    .from(PATTERNS_TABLE)
    .update({ is_deleted: true })
    .eq('pattern_id', patternId)
    .select()
  return { data, error }
}

export const getUserPatternDetails = async ({
  patternId,
}: {
  patternId: string
}): Promise<SupabaseResponse<AgamiPattern>> => {
  if (!patternId) {
    return { error: 'Missing patternId' }
  }

  const { data, error } = await supabase.functions.invoke('get-pattern-details', {
    body: JSON.stringify({ patternId }),
  })

  if (!error) {
    return { data }
  }

  const parsedError = await parseError(error)
  await recoverInvalidSession(error)

  return { data, error: parsedError }
}

export const removeAgamiFromPattern = async ({
  agamiId,
  patternId,
  experienceId,
}: {
  agamiId: string
  patternId: string
  experienceId: string
}): Promise<DatabaseInsertResult> => {
  if (!agamiId || !patternId || !experienceId) {
    return { error: { message: 'Missing agamiId, patternId or experienceId' } as PostgrestError }
  }
  const { data, error } = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    .update({ pattern_id: null })
    .eq('pattern_id', patternId)
    .eq('experience_id', experienceId)
    .eq('spot_id', agamiId)
    .select()
  return { data, error }
}

export const addAgamiToPattern = async ({
  agamiId,
  patternId,
  experienceId,
}: {
  agamiId: string
  patternId: string
  experienceId: string
}): Promise<DatabaseInsertResult> => {
  if (!agamiId || !patternId || !experienceId) {
    return { error: { message: 'Missing agamiId, patternId or experienceId' } as PostgrestError }
  }
  const { data, error } = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    .update({ pattern_id: patternId })
    .eq('experience_id', experienceId)
    .eq('spot_id', agamiId)
    .select()
  return { data, error }
}

export const generateAgamiMediaSignedUrl = async ({
  mediaPath,
  mediaType,
  expiresIn,
}: {
  mediaPath: string
  mediaType: number
  expiresIn: number
}): Promise<{ data: { signedUrl: string } } | { error: string }> => {
  if (!mediaPath) {
    return {
      error: 'Specify the path to the Agami audio content to generate a signed URL.',
    }
  }

  if (!expiresIn || !isNumber(expiresIn)) {
    return {
      error: 'Provide the validity duration (in seconds) for the Agami audio signed URL.',
    }
  }

  if (expiresIn > MAX_SIGNED_URL_VALIDITY) {
    return {
      error: 'Unable to generate URLs with a validity period exceeding 30 days.',
    }
  }

  const { data, error } = await supabase.storage
    .from(mediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET)
    .createSignedUrl(mediaPath, expiresIn)

  if (error) {
    return { error: error.message }
  }

  return { data: { signedUrl: data.signedUrl } }
}

export const getMediaTypes = async (): Promise<DatabaseSelectResult> => {
  const { data, error } = await supabase.from('spot_media_type').select('*')
  return { data, error }
}

export const deleteAgamiMedia = async ({
  audioPath,
  contentMediaType,
}: {
  audioPath: string
  contentMediaType: number
}): Promise<void> => {
  if (!audioPath) return
  try {
    await removeFile({
      filename: audioPath,
      bucketId: contentMediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
    })
  } catch (err) {
    logger.error(err, 'DeleteAgamiMedia')
  }
}

export const deleteAgamiFromTable = async (id: string): Promise<void> => {
  if (!id) return
  try {
    await supabase.from(GEOXP_SPOTS_TABLE).delete().eq('id', id)
  } catch (err) {
    logger.error(err, 'DeleteAgamiFromTable')
  }
}
